<template>
    <div
        v-if="isDragging && dragType"
        class="field-ghost-preview"
        :class="[`ghost-${dragType}`, { 'ghost-valid': isValidDropZone }]"
        :style="ghostStyle"
    >
        <div class="ghost-icon">
            <i :class="getFieldIcon(dragType)"></i>
        </div>
        <div class="ghost-label">
            {{ getFieldLabel(dragType) }}
        </div>
        <div class="ghost-dimensions">
            {{ getDimensions(dragType) }}
        </div>
    </div>
</template>

<script>
export default {
    name: 'FieldGhostPreview',
    props: {
        isDragging: {
            type: Boolean,
            default: false
        },
        dragType: {
            type: String,
            default: null,
            validator: (value) => !value || ['signature', 'text', 'select', 'date'].includes(value)
        },
        cursorPosition: {
            type: Object,
            default: () => ({ x: 0, y: 0 })
        },
        isValidDropZone: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        ghostStyle() {
            // Offset the ghost slightly from cursor for better visibility
            const offsetX = 16;
            const offsetY = 16;

            return {
                left: `${this.cursorPosition.x + offsetX}px`,
                top: `${this.cursorPosition.y + offsetY}px`
            };
        }
    },
    methods: {
        getFieldIcon(type) {
            const icons = {
                signature: 'fa fa-signature',
                text: 'fa fa-font',
                select: 'fa fa-list',
                date: 'fa fa-calendar'
            };
            return icons[type] || 'fa fa-question';
        },

        getFieldLabel(type) {
            const labels = {
                signature: 'Signature Field',
                text: 'Text Field',
                select: 'Select Field',
                date: 'Date Field'
            };
            return labels[type] || 'Field';
        },

        getDimensions(type) {
            // Default dimensions for each field type
            const dimensions = {
                signature: '200 x 60',
                text: '150 x 30',
                select: '150 x 40',
                date: '150 x 40'
            };
            return dimensions[type] || '150 x 40';
        }
    }
};
</script>

<style scoped>
.field-ghost-preview {
    position: fixed;
    pointer-events: none;
    z-index: 10000;

    /* Semi-transparent appearance */
    opacity: 0.85;

    /* Dashed border */
    border: 2px dashed;
    border-radius: 6px;

    /* Padding and spacing */
    padding: 12px 16px;
    min-width: 140px;

    /* Shadow for depth */
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    /* Smooth transitions */
    transition: opacity 100ms ease-out;

    /* Layout */
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;

    /* Background */
    background-color: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
}

/* Field type specific colors */
.field-ghost-preview.ghost-signature {
    border-color: #3b82f6;
    background-color: rgba(59, 130, 246, 0.12);
}

.field-ghost-preview.ghost-text {
    border-color: #10b981;
    background-color: rgba(16, 185, 129, 0.12);
}

.field-ghost-preview.ghost-select {
    border-color: #9f7aea;
    background-color: rgba(159, 122, 234, 0.12);
}

.field-ghost-preview.ghost-date {
    border-color: #f59e0b;
    background-color: rgba(245, 158, 11, 0.12);
}

/* Valid drop zone state */
.field-ghost-preview.ghost-valid {
    opacity: 0.9;
    border-style: solid;
}

/* Ghost icon */
.ghost-icon {
    font-size: 28px;
    margin-bottom: 2px;
}

.ghost-signature .ghost-icon {
    color: #3b82f6;
}

.ghost-text .ghost-icon {
    color: #10b981;
}

.ghost-select .ghost-icon {
    color: #9f7aea;
}

.ghost-date .ghost-icon {
    color: #f59e0b;
}

/* Ghost label */
.ghost-label {
    font-size: 13px;
    font-weight: 600;
    color: #2d3748;
    text-align: center;
    line-height: 1.2;
}

/* Ghost dimensions */
.ghost-dimensions {
    font-size: 10px;
    font-weight: 500;
    color: #718096;
    font-family: 'Courier New', monospace;
    padding: 2px 6px;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
}

/* Animation on appear */
@keyframes ghostAppear {
    from {
        opacity: 0;
        transform: scale(0.9);
    }
    to {
        opacity: 0.85;
        transform: scale(1);
    }
}

.field-ghost-preview {
    animation: ghostAppear 150ms ease-out;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
    .field-ghost-preview {
        padding: 10px 14px;
        min-width: 120px;
    }

    .ghost-icon {
        font-size: 24px;
    }

    .ghost-label {
        font-size: 12px;
    }

    .ghost-dimensions {
        font-size: 9px;
    }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
    .field-ghost-preview {
        border-width: 3px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    }

    .ghost-label {
        font-weight: 700;
    }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
    .field-ghost-preview {
        animation: none;
        transition: none;
    }
}

/* Print styles - hide ghost preview */
@media print {
    .field-ghost-preview {
        display: none;
    }
}
</style>
